import {useEffect, useState} from 'react';
import {
	Card,
	Container,
	Divider,
	List,
	ListItem,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	Stack,
	Typography,
} from '@mui/material';
import DragHandleIcon from '@mui/icons-material/DragHandle';
import {Container as DContainer, Draggable} from 'react-smooth-dnd';
import {toast} from 'react-toastify';
import {cloneDeep} from 'lodash';
import Page from 'src/components/Page';
import PlusButton from 'src/components/buttons/PlusButton';
import ActionMenu from 'src/components/ui/ActionMenu';
import {useModal} from 'src/hooks/useModal';
import useCategories from 'src/hooks/useCategories';
import {useDndScrollWorkaround} from 'src/hooks/useDndScrollWorkaround';
import {askYesOrNo, askForOption} from 'src/components/modals/ChoicesModal';
import {arrayMoveImmutable} from 'src/utils/dndUtils';
import {handleRequestError} from 'src/utils/helperMethods';
import {
	getHomePageBlocks,
	deleteHomePageBlock,
	reorderHomePageBlocks,
} from 'src/utils/AdminApi';
import BlockForm from './BlockForm';
import {BLOCK_TYPES, BLOCK_TYPE_LABELS, DEFAULT_BLOCK_DATA, getBlockPreviewText} from './blockTypes';

export default function HomePageBlocksPage() {
	const {open, close} = useModal();
	const {categories} = useCategories();
	const [blocks, setBlocks] = useState([]);
	useDndScrollWorkaround();

	const fetchBlocks = async () => {
		try {
			const response = await getHomePageBlocks();
			setBlocks(response ?? []);
		} catch (err) {
			// backend not implemented yet, start from an empty page
			setBlocks([]);
		}
	};

	useEffect(() => {
		fetchBlocks();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const onDrop = async ({removedIndex, addedIndex}) => {
		const oldArray = cloneDeep(blocks);
		const newArray = arrayMoveImmutable(blocks, removedIndex, addedIndex);
		setBlocks(newArray);

		try {
			await reorderHomePageBlocks(newArray.map((b) => b.id));
		} catch (err) {
			toast.error(handleRequestError(err));
			setBlocks(oldArray);
		}
	};

	const openBlockForm = (type, initialData) => {
		open({
			title: initialData ? 'تعديل البلوك' : `اضافة ${BLOCK_TYPE_LABELS[type]}`,
			children: (
				<BlockForm
					type={type}
					initialData={initialData}
					onSuccess={(block) => {
						setBlocks((prev) =>
							initialData
								? prev.map((b) => (b.id === block.id ? block : b))
								: [...prev, block],
						);
						close();
					}}
				/>
			),
		});
	};

	const addBlock = () => {
		askForOption(
			'اختر نوع البلوك',
			Object.values(BLOCK_TYPES).map((type) => ({
				id: type,
				text: BLOCK_TYPE_LABELS[type],
				variant: 'outlined',
				action: () => openBlockForm(type, {data: DEFAULT_BLOCK_DATA[type]}),
			})),
		);
	};

	const removeBlock = async (block) => {
		const confirmed = await askYesOrNo('هل تريد تأكيد حذف هذا البلوك؟');
		if (!confirmed) return;

		try {
			await deleteHomePageBlock(block.id);
			setBlocks((prev) => prev.filter((b) => b.id !== block.id));
		} catch (err) {
			toast.error(handleRequestError(err));
		}
	};

	return (
		<Page title="بلوكات الصفحة الرئيسية">
			<Container maxWidth="md">
				<Card sx={{py: 2, px: {xs: 1, sm: 3, md: 5}}}>
					<Stack sx={{mb: 1}} justifyContent="space-between" alignItems="center" direction="row">
						<Typography mt={1} variant="h4">
							بلوكات الصفحة الرئيسية
						</Typography>

						<PlusButton onClick={addBlock} />
					</Stack>

					<Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
						تحكم بترتيب ومحتوى البلوكات المعروضة في الصفحة الرئيسية للمتجر
					</Typography>

					<List>
						<DContainer
							lockAxis="y"
							dragHandleSelector=".drag-handle"
							onDrop={onDrop}
						>
							{blocks.map((block) => (
								<Draggable key={block.id}>
									<ListItem sx={{px: 0}}>
										<ListItemButton sx={{px: 0}} className="drag-handle">
											<ListItemText
												primary={BLOCK_TYPE_LABELS[block.type]}
												secondary={getBlockPreviewText(block, categories)}
												secondaryTypographyProps={{noWrap: true}}
											/>

											<ActionMenu
												variant="text"
												title="اجراء"
												items={[
													{
														id: 'edit',
														text: 'تعديل',
														action: () => openBlockForm(block.type, block),
													},
													{
														id: 'delete',
														text: 'حذف',
														action: () => removeBlock(block),
													},
												]}
											/>

											<ListItemIcon sx={{ml: 2, cursor: 'move'}}>
												<DragHandleIcon />
											</ListItemIcon>
										</ListItemButton>
									</ListItem>
									<Divider />
								</Draggable>
							))}
						</DContainer>
					</List>

					{blocks.length === 0 && (
						<Typography color="text.secondary" textAlign="center" sx={{py: 4}}>
							لا يوجد بلوكات مضافة بعد
						</Typography>
					)}
				</Card>
			</Container>
		</Page>
	);
}
